import {
  Box,
  Image,
  Spinner,
  useColorModeValue,
  Text,
  HStack,
} from "@chakra-ui/react";
import React from "react";
import { useParams } from "react-router-dom";
import { useGetVideoQuery, useGetSegmentsQuery } from "services/api";
import Card from "components/Card/Card";
import { formatTime } from "utilities/time";

export const VideoCard = ({
  onVideoClick,
  videoId,
  confidenceCounts,
  customSegments = [],
}) => {
  const { projectId } = useParams();
  const { data, error, isLoading } = useGetVideoQuery({
    projectId,
    videoId,
  });

  const { data: segmentData } = useGetSegmentsQuery(
    {
      projectId,
      filename: data?.system_metadata?.filename,
    },
    {
      skip: !data?.system_metadata?.filename,
    }
  );

  const thumbnailUrl = data?.hls?.thumbnail_urls?.[0];
  const duration = data?.system_metadata?.duration;
  const cardBg = useColorModeValue("white", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  const handleCardClick = () => {
    onVideoClick(videoId);
  };

  // Calculate total segment count
  const totalSegments =
    (segmentData?.segments?.length || 0) + (customSegments?.length || 0);

  if (!thumbnailUrl) {
    return (
      <Card
        _hover={{ bg: hoverBg }}
        alignItems="center"
        bg={cardBg}
        border="1px solid"
        borderColor="gray.200"
        cursor="pointer"
        display="flex"
        justifyContent="center"
        pb="56.25%"
        position="relative"
        transition="all 0.2s"
        w="100%"
      />
    );
  }

  if (error) {
    return (
      <Card
        _hover={{ bg: hoverBg }}
        alignItems="center"
        bg={cardBg}
        border="1px solid"
        borderColor="gray.200"
        cursor="pointer"
        display="flex"
        justifyContent="center"
        pb="56.25%"
        position="relative"
        transition="all 0.2s"
        w="100%"
      >
        <Box
          color="red.500"
          left="50%"
          position="absolute"
          top="50%"
          transform="translate(-50%, -50%)"
        >
          Error: {error.error}
        </Box>
      </Card>
    );
  }

  return (
    <Card
      _hover={{
        transform: "scale(1.02)",
        borderRadius: "3xl",
        transition: "all 0.2s ease-in-out",
      }}
      bg={cardBg}
      cursor="pointer"
      onClick={handleCardClick}
      overflow="hidden"
      p={0}
      position="relative"
      transition="all 0.2s ease-in-out"
      w="100%"
    >
      {isLoading ? (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="center"
          pb="56.25%"
          position="relative"
          w="100%"
        >
          <Spinner
            color="blue.500"
            left="50%"
            position="absolute"
            size="xl"
            top="50%"
            transform="translate(-50%, -50%)"
          />
        </Box>
      ) : (
        <Box position="relative">
          <Image
            alt="Video thumbnail"
            aspectRatio="16/9"
            h="auto"
            objectFit="cover"
            src={thumbnailUrl}
            w="100%"
          />

          <HStack
            bg="rgba(0, 0, 0, 0.55)"
            borderRadius="4px"
            left="8px"
            position="absolute"
            px="6px"
            py="2px"
            spacing={1}
            top="8px"
          >
            <Text color="white" fontSize="xs" fontWeight="medium">
              {data?.system_metadata?.filename}
            </Text>
            {!!confidenceCounts && (
              <>
                {confidenceCounts.high > 0 && (
                  <Text color="green.300" fontSize="xs" fontWeight="medium">
                    {confidenceCounts.high}H
                  </Text>
                )}
                {confidenceCounts.medium > 0 && (
                  <Text color="orange.300" fontSize="xs" fontWeight="medium">
                    {confidenceCounts.medium}M
                  </Text>
                )}
                {confidenceCounts.low > 0 && (
                  <Text color="red.300" fontSize="xs" fontWeight="medium">
                    {confidenceCounts.low}L
                  </Text>
                )}
              </>
            )}
          </HStack>

          {!!duration && (
            <Box
              bg="rgba(0, 0, 0, 0.55)"
              borderRadius="4px"
              bottom="8px"
              color="white"
              fontSize="sm"
              fontWeight="medium"
              position="absolute"
              px="6px"
              py="2px"
              right="8px"
            >
              {formatTime(duration)}
            </Box>
          )}
          <Box
            bg="rgba(0, 0, 0, 0.55)"
            borderRadius="4px"
            color="white"
            fontSize="xs"
            fontWeight="medium"
            position="absolute"
            px="6px"
            py="2px"
            right="8px"
            top="8px"
          >
            {totalSegments} segment
            {totalSegments === 1 ? "" : "s"}
          </Box>
        </Box>
      )}
    </Card>
  );
};
